// ABOUTME: Slide-out drawer showing the active cart's line items with quantity edit and remove.
// ABOUTME: Offers actions to add products (catalog), discard the cart, and convert it to an offer.

import {
    AddIcon,
    Box,
    Button,
    DeleteIcon,
    Divider,
    Drawer,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import { useGetNodeQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CartCatalogDialog } from './CartCatalogDialog';
import { useCartContext } from './CartContext';
import { ConvertCartDialog } from './ConvertCartDialog';

type LineItemContent = {
    contentTypeGroup: string;
    contentType: string;
    value: Array<{ value: string[] }>;
};

function readContent(item: Record<string, unknown>, group: string, field: string): string {
    const contents = (item.contents ?? []) as LineItemContent[];
    const content = contents.find(c => c.contentTypeGroup === group && c.contentType === field);
    return content?.value?.[0]?.value?.[0] ?? '';
}

export function CartDrawer() {
    const { formatMessage } = useIntl();
    const cart = useCartContext();
    const settings = useContext(SettingsContext);
    const prototypeHierarchyId = settings.prototype?.hierarchies?.prototype;
    const modelHierarchyId = settings.prototype?.hierarchies?.model;

    const [catalogOpen, setCatalogOpen] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);
    const [qtyDraft, setQtyDraft] = useState<Record<string, string>>({});

    const lineItems = cart?.cart?.lineItems ?? [];

    const prototypeIds = useMemo(() => {
        const ids = new Set<string>();
        lineItems.forEach(item => {
            const id = readContent(item, 'line_item_info', 'prototype');
            if (id) ids.add(id);
        });
        return Array.from(ids);
    }, [lineItems]);

    const { data: prototypeNodesData } = useGetNodeQuery({
        variables: {
            hierarchiesId: prototypeHierarchyId,
            nodeFilter: { ids: prototypeIds },
        },
        fetchPolicy: 'cache-first',
        skip: prototypeIds.length === 0 || !prototypeHierarchyId,
    });

    const prototypeIdToCode = useMemo(() => {
        const map: Record<string, string> = {};
        const nodes = (prototypeNodesData?.hierarchies?.[0]?.nodes ?? []) as Array<{
            id: string;
            code?: string | null;
        }>;
        nodes.forEach(node => {
            if (node.code) map[node.id] = node.code;
        });
        return map;
    }, [prototypeNodesData]);

    // Non-configurable items reference the product directly via the `model` link; resolve
    // their names from the model nodes.
    const modelIds = useMemo(() => {
        const ids = new Set<string>();
        lineItems.forEach(item => {
            const id = readContent(item, 'line_item_info', 'model');
            if (id) ids.add(id);
        });
        return Array.from(ids);
    }, [lineItems]);

    const { data: modelNodesData } = useGetNodeQuery({
        variables: {
            hierarchiesId: modelHierarchyId,
            nodeFilter: { ids: modelIds },
        },
        fetchPolicy: 'cache-first',
        skip: modelIds.length === 0 || !modelHierarchyId,
    });

    const modelIdToCode = useMemo(() => {
        const map: Record<string, string> = {};
        const nodes = (modelNodesData?.hierarchies?.[0]?.nodes ?? []) as Array<{
            id: string;
            code?: string | null;
        }>;
        nodes.forEach(node => {
            if (node.code) map[node.id] = node.code;
        });
        return map;
    }, [modelNodesData]);

    if (!cart || !cart.enabled) {
        return null;
    }

    const handleQtyCommit = (lineItemId: string) => {
        const raw = qtyDraft[lineItemId];
        if (raw === undefined) return;
        const quantity = parseInt(raw, 10);
        setQtyDraft(prev => {
            const next = { ...prev };
            delete next[lineItemId];
            return next;
        });
        if (Number.isFinite(quantity) && quantity >= 1) {
            cart.updateItem(lineItemId, { quantity });
        }
    };

    return (
        <>
            <Drawer anchor="right" open={cart.isDrawerOpen} onClose={cart.closeDrawer}>
                <Box
                    width={420}
                    maxWidth="100vw"
                    display="flex"
                    flexDirection="column"
                    height="100%"
                >
                    <Box p={2}>
                        <Typography variant="h4">
                            {formatMessage({ id: 'cpq.cart.title', defaultMessage: 'Cart' })}
                        </Typography>
                    </Box>
                    <Box>
                        <Divider />
                    </Box>

                    <Box flexGrow={1} overflow="auto" p={2}>
                        {lineItems.length === 0 ? (
                            <Typography color="textSecondary">
                                {formatMessage({
                                    id: 'cpq.cart.empty',
                                    defaultMessage: 'Your cart is empty.',
                                })}
                            </Typography>
                        ) : (
                            <Stack gap={2}>
                                {lineItems.map(item => {
                                    const lineItemId = item.id as string;
                                    const prototypeId = readContent(
                                        item,
                                        'line_item_info',
                                        'prototype',
                                    );
                                    const modelId = readContent(item, 'line_item_info', 'model');
                                    const label =
                                        prototypeIdToCode[prototypeId] ??
                                        modelIdToCode[modelId] ??
                                        (item.code as string) ??
                                        lineItemId;
                                    const quantity = readContent(
                                        item,
                                        'line_item_info',
                                        'quantity',
                                    );
                                    const lineTotal = readContent(
                                        item,
                                        'line_item_pricing',
                                        'line_total',
                                    );
                                    return (
                                        <Box
                                            key={lineItemId}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Box flexGrow={1}>
                                                <Typography variant="body1">{label}</Typography>
                                                {lineTotal && (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {lineTotal}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box width={72}>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={qtyDraft[lineItemId] ?? quantity}
                                                    onChange={e =>
                                                        setQtyDraft(prev => ({
                                                            ...prev,
                                                            [lineItemId]: e.target.value,
                                                        }))
                                                    }
                                                    onBlur={() => handleQtyCommit(lineItemId)}
                                                    fullWidth
                                                />
                                            </Box>
                                            <IconButton
                                                aria-label={formatMessage({
                                                    id: 'common.remove',
                                                    defaultMessage: 'Remove',
                                                })}
                                                onClick={() => cart.removeItem(lineItemId)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>

                    <Box>
                        <Divider />
                    </Box>
                    <Stack gap={1} p={2}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={() => setCatalogOpen(true)}
                        >
                            {formatMessage({
                                id: 'cpq.cart.addProducts',
                                defaultMessage: 'Add products',
                            })}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => setConvertOpen(true)}
                            disabled={lineItems.length === 0}
                        >
                            {formatMessage({
                                id: 'cpq.cart.convertToOffer',
                                defaultMessage: 'Convert to offer',
                            })}
                        </Button>
                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => cart.discard()}
                            disabled={!cart.cart}
                        >
                            {formatMessage({
                                id: 'cpq.cart.discard',
                                defaultMessage: 'Discard cart',
                            })}
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            <CartCatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} />
            <ConvertCartDialog open={convertOpen} onClose={() => setConvertOpen(false)} />
        </>
    );
}
